import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/button';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Badge } from '@/shared/components/ui/badge';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { useTranslation } from 'react-i18next';

export interface Column {
  id: string;
  name: string;
  type?: 'text' | 'date';
}

export interface FilterOption {
  id: string;
  name: string;
  options: {
    value: string;
    label: string;
  }[];
  type?: 'text' | 'date';
}

export interface FilterState {
  [key: string]: string[];
}

export interface ColumnState {
  [key: string]: boolean;
}

interface CategoryFilterProps {
  trigger: React.ReactNode;
  selectedFilters?: FilterState;
  onChange?: (filters: FilterState) => void;
  columns?: Column[];
  selectedColumns?: ColumnState;
  onColumnChange?: (columns: ColumnState) => void;
  onClear?: () => void;
}

export function CategoryFilter({
  trigger,
  selectedFilters = {},
  onChange,
  columns,
  selectedColumns,
  onColumnChange,
  onClear,
}: CategoryFilterProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [localSelectedColumns, setLocalSelectedColumns] = useState<ColumnState>({});
  const [_selectedColumnType, setSelectedColumnType] = useState<'date' | 'text' | null>(null);
  const [localSelectedFilters, setLocalSelectedFilters] = useState<FilterState>(selectedFilters);

  useEffect(() => {
    setLocalSelectedFilters(selectedFilters || {});
    
    if (columns) {
      const hasSelectedColumn = selectedColumns && Object.values(selectedColumns).some(value => value === true);
      
      if (!hasSelectedColumn && columns.length > 0) {
        const newSelectedColumns = selectedColumns ? { ...selectedColumns } : {};
        newSelectedColumns[columns[0].id] = true;
        
        setLocalSelectedColumns(newSelectedColumns);
        
        if (onColumnChange) {
          onColumnChange(newSelectedColumns);
        }
        
        const newFilters = { ...selectedFilters };
        newFilters[columns[0].id] = ['active'];
        
        setLocalSelectedFilters(newFilters);
        
        if (onChange) {
          onChange(newFilters);
        }
      } else if (selectedColumns) {
        setLocalSelectedColumns({...selectedColumns});
        
        const newFilters = { ...selectedFilters };
        let filtersUpdated = false;
        
        for (const column of columns) {
          if (selectedColumns[column.id] && !newFilters[column.id]) {
            newFilters[column.id] = ['active'];
            filtersUpdated = true;
          }
        }
        
        if (filtersUpdated && onChange) {
          setLocalSelectedFilters(newFilters);
          onChange(newFilters);
        }
      }
    }
  }, [selectedFilters, selectedColumns, columns, onChange, onColumnChange]);

  useEffect(() => {
    if (open) {
      if (selectedColumns) {
        setLocalSelectedColumns({...selectedColumns});
        
        if (columns && onChange) {
          const newFilters = { ...localSelectedFilters };
          
          for (const column of columns) {
            if (selectedColumns[column.id] && !newFilters[column.id]) {
              newFilters[column.id] = ['active'];
            }
          }
          
          for (const columnId in newFilters) {
            if (!selectedColumns[columnId]) {
              delete newFilters[columnId];
            }
          }
          
          setLocalSelectedFilters(newFilters);
          onChange(newFilters);
        }
        
        if (columns) {
          let hasDateSelected = false;
          let hasTextSelected = false;
          
          for (const column of columns) {
            if (selectedColumns[column.id]) {
              if (column.type === 'date') {
                hasDateSelected = true;
              } else {
                hasTextSelected = true;
              }
            }
          }
          
          if (hasDateSelected && !hasTextSelected) {
            setSelectedColumnType('date');
          } else if (!hasDateSelected && hasTextSelected) {
            setSelectedColumnType('text');
          } else if (hasDateSelected && hasTextSelected) {
            setSelectedColumnType(null);
          } else {
            setSelectedColumnType(null);
          }
        }
      } else if (columns) {
        const initialState = columns.reduce((acc, column) => {
          acc[column.id] = false;
          return acc;
        }, {} as ColumnState);
        setLocalSelectedColumns(initialState);
        setSelectedColumnType(null);
      }
    }
  }, [open, selectedColumns, columns, localSelectedFilters, onChange]);

  const handleColumnChange = (columnId: string, checked: boolean, columnType?: 'text' | 'date') => {
    const newSelectedColumns = {
      ...localSelectedColumns,
    };
    
    if (checked && columnType && columns) {
      if (columnType === 'date') {
        columns.forEach(column => {
          if (column.type === 'text') {
            newSelectedColumns[column.id] = false;
          }
        });
        newSelectedColumns[columnId] = true;
      } 
      else if (columnType === 'text') {
        columns.forEach(column => {
          if (column.type === 'date') {
            newSelectedColumns[column.id] = false;
          }
        });
        newSelectedColumns[columnId] = true;
      }
    } 
    else if (!checked && columns) {
      newSelectedColumns[columnId] = false;
      
      const wouldAnyBeSelected = Object.values(newSelectedColumns).some(Boolean);
      if (!wouldAnyBeSelected) {
        return;
      }
    }
    
    setLocalSelectedColumns(newSelectedColumns);
    
    if (checked && columnType) {
      setSelectedColumnType(columnType);
    } else if (!checked && columnType && columns) {
      let hasOtherSameTypeSelected = false;
      let hasOtherDifferentTypeSelected = false;
      
      for (const column of columns) {
        if (column.id !== columnId && newSelectedColumns[column.id]) {
          if (column.type === columnType) {
            hasOtherSameTypeSelected = true;
          } else {
            hasOtherDifferentTypeSelected = true;
          }
        }
      }
      
      if (!hasOtherSameTypeSelected && !hasOtherDifferentTypeSelected) {
        setSelectedColumnType(null);
      } else if (!hasOtherSameTypeSelected && hasOtherDifferentTypeSelected) {
        setSelectedColumnType(columnType === 'date' ? 'text' : 'date');
      }
    }
    
    if (onColumnChange) {
      onColumnChange(newSelectedColumns);
    }
    
    if (onChange) {
      const newFilters = { ...localSelectedFilters };
      
      if (checked) {
        newFilters[columnId] = ['active'];
      } else {
        delete newFilters[columnId];
      }
      
      setLocalSelectedFilters(newFilters);
      onChange(newFilters);
    }
  };
  
  const handleClear = () => {
    const emptyFilters = {} as FilterState;
    
    if (onChange && columns && columns.length > 0) {
      emptyFilters[columns[0].id] = ['active'];
      onChange(emptyFilters);
    }
    
    setLocalSelectedFilters(emptyFilters);
    
    if (columns && onColumnChange) {
      const resetColumns = columns.reduce((acc, column) => {
        acc[column.id] = column.id === columns[0].id;
        return acc;
      }, {} as ColumnState);
      
      setLocalSelectedColumns(resetColumns);
      onColumnChange(resetColumns);
    }
    
    setSelectedColumnType(null);
    console.log('[CategoryFilter] ' + t('logs.clearingSelectedColumnType', 'Clearing selected column type'));
    
    setOpen(false);
    
    if (onClear) {
      onClear();
    }
  };

  const badgeCount = Object.values(localSelectedFilters || {}).reduce((count, values) => count + values.length, 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          {trigger}
          {badgeCount > 0 && (
            <Badge
              variant="secondary"
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center"
            >
              {badgeCount}
            </Badge>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <h3 className="font-medium">{t('common.pages.knowledge.database.fields.filterBy', 'Colunas Disponíveis')}</h3>
          <Button variant="ghost" size="sm" onClick={handleClear}>
            {t('common.actions.reset', 'Restaurar')}
          </Button>
        </div>
        <ScrollArea className="h-[300px] p-4">
          <div className="space-y-4">
            {columns && (
              <div className="space-y-2">
                <div className="grid grid-cols-1 gap-2">
                  {columns.map((column) => (
                    <div key={column.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`column-${column.id}`}
                        checked={localSelectedColumns[column.id] !== false}
                        onCheckedChange={(checked) => {
                          handleColumnChange(column.id, !!checked, column.type);
                        }}
                      />
                      <Label
                        htmlFor={`column-${column.id}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {column.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
